import React, { useState, useEffect, useRef } from 'react';
import { FiClock, FiShield, FiStar, FiTrendingUp, FiCheck, FiInstagram, FiTag, FiArrowRight, FiBox, FiTruck, FiCreditCard, FiShoppingBag, FiMusic, FiPause, FiPlay, FiVolume2, FiVolumeX } from 'react-icons/fi';

const TeaserPage: React.FC = () => {
  // State for countdown timer
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  // State for email subscription
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // State for hover effects
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  
  // State for audio player
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(true);
  const youtubePlayerRef = useRef<HTMLIFrameElement>(null);
  
  // Cursor follower effect - only for desktop
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursorTextRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Parallax effect for background elements - optimized for mobile
  const [scrollY, setScrollY] = useState(0);
  const [lastScrollTime, setLastScrollTime] = useState(0);
  
  // Launch date - set this to your actual launch date
  const launchDate = new Date('2024-12-31T00:00:00');
  
  // State for image loading
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Check if device is mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Calculate time remaining
  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +launchDate - +new Date();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    
    // Calculate immediately and then set up interval
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Additional optimizations for mobile
  useEffect(() => {
    if (isMobile) {
      // Add viewport meta tag to ensure proper scaling
      let viewportMeta = document.querySelector('meta[name="viewport"]');
      if (!viewportMeta) {
        viewportMeta = document.createElement('meta');
        viewportMeta.setAttribute('name', 'viewport');
        document.head.appendChild(viewportMeta);
      }
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
      
      // Disable certain animations on mobile for better performance
      document.documentElement.classList.add('reduce-motion');
      
      // Disable hover effects on mobile to prevent tap issues
      document.documentElement.classList.add('no-hover');
    }
    
    // Clean up on unmount
    return () => {
      if (isMobile) {
        document.documentElement.classList.remove('reduce-motion');
        document.documentElement.classList.remove('no-hover');
      }
    };
  }, [isMobile]);
  
  // Throttle mousemove events for better performance
  useEffect(() => {
    // Skip on mobile devices
    if (isMobile) return;
    
    let timeoutId: NodeJS.Timeout | null = null;
    let lastMouseX = 0;
    let lastMouseY = 0;
    
    const handleMouseMove = (e: MouseEvent) => {
      // Only update if mouse has moved significantly or enough time has passed
      const movedSignificantly = Math.abs(e.clientX - lastMouseX) > 5 || Math.abs(e.clientY - lastMouseY) > 5;
      
      if (movedSignificantly && !timeoutId) {
        lastMouseX = e.clientX;
        lastMouseY = e.clientY;
        
        setMousePosition({ x: e.clientX, y: e.clientY });
        
        // Update text cursor position with lag
        if (cursorTextRef.current) {
          setTimeout(() => {
            if (cursorTextRef.current) {
              cursorTextRef.current.style.transform = `translate(${e.clientX + 15}px, ${e.clientY - 15}px)`;
            }
          }, 100);
        }
        
        // Throttle updates
        timeoutId = setTimeout(() => {
          timeoutId = null;
        }, 16); // approximately 60fps
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isMobile]);
  
  // Update cursor follower position - only on desktop
  useEffect(() => {
    if (isMobile || !cursorRef.current) return;
    
    cursorRef.current.style.transform = `translate(${mousePosition.x}px, ${mousePosition.y}px)`;
  }, [mousePosition, isMobile]);
  
  // Handle parallax on scroll with throttling for better mobile performance
  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now();
      
      // Throttle scroll events on mobile
      if (isMobile && now - lastScrollTime < 50) {
        return;
      }
      
      setScrollY(window.scrollY);
      setLastScrollTime(now);
    };
    
    // Add passive event listener to improve scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, lastScrollTime]);
  
  // Auto-advance active section with reduced frequency on mobile
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSectionIndex(prev => (prev + 1) % 3);
    }, isMobile ? 8000 : 5000); // Slower rotation on mobile
    
    return () => clearInterval(timer);
  }, [isMobile]);
  
  // Initialize animation after mount - optimized for mobile
  useEffect(() => {
    // Set a timeout to add the animation class to all elements
    const elements = document.querySelectorAll('[data-aos]');
    
    const observerOptions = {
      threshold: isMobile ? 0.05 : 0.1, // Lower threshold on mobile for earlier trigger
      rootMargin: isMobile ? '30px' : '0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aos-animate');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    elements.forEach(element => {
      observer.observe(element);
    });
    
    return () => {
      elements.forEach(element => {
        observer.unobserve(element);
      });
    };
  }, [isMobile]);
  
  // Handle audio player
  useEffect(() => {
    // Initialize the iframe reference
    const youtubeIframe = youtubePlayerRef.current;
    
    // Set initial volume and play state when iframe loads
    if (youtubeIframe) {
      youtubeIframe.onload = () => {
        // Set initial state if needed
      };
    }
    
    return () => {
      // Cleanup if needed
    };
  }, []);
  
  // Toggle play/pause
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    
    // Get the iframe element
    const iframe = youtubePlayerRef.current;
    if (!iframe) return;
    
    try {
      // Create message based on play state
      const action = isPlaying ? 'pauseVideo' : 'playVideo';
      const message = JSON.stringify({
        event: 'command',
        func: action,
        args: []
      });
      
      // Post message to iframe
      iframe.contentWindow?.postMessage(message, '*');
    } catch (error) {
      console.error('Error controlling YouTube player:', error);
    }
  };
  
  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
    
    // Get the iframe element
    const iframe = youtubePlayerRef.current;
    if (!iframe) return;
    
    try {
      // Create message based on mute state
      const action = isMuted ? 'unMute' : 'mute';
      const message = JSON.stringify({
        event: 'command',
        func: action,
        args: []
      });
      
      // Post message to iframe
      iframe.contentWindow?.postMessage(message, '*');
    } catch (error) {
      console.error('Error controlling YouTube player:', error);
    }
  };
  
  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (isMuted && newVolume > 0) setIsMuted(false);
    
    // Get the iframe element
    const iframe = youtubePlayerRef.current;
    if (!iframe) return;
    
    try {
      // Create message to set volume
      const message = JSON.stringify({
        event: 'command',
        func: 'setVolume',
        args: [newVolume * 100]
      });
      
      // Post message to iframe
      iframe.contentWindow?.postMessage(message, '*');
    } catch (error) {
      console.error('Error setting YouTube volume:', error);
    }
  };
  
  // Handle email subscription
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setEmailError('');
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    try {
      // Show loading state
      setSubscribed(false);
      
      // Use the new direct endpoint
      const response = await fetch('/api/direct-db-subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubscribed(true);
        setEmail('');
        // Reset form after 3 seconds
        setTimeout(() => {
          setSubscribed(false);
        }, 3000);
      } else {
        setEmailError(data.message || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      setEmailError('An error occurred. Please try again later.');
    }
  };
  
  // Add image preloading for mobile
  useEffect(() => {
    // Preload critical images
    const preloadImages = () => {
      const imagesToPreload = ['/494858416_2170860486681456_7057137133348816565_n.jpg'];
      let loadedCount = 0;
      
      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount === imagesToPreload.length) {
          setImagesLoaded(true);
        }
      };
      
      imagesToPreload.forEach(src => {
        const img = new Image();
        img.onload = checkAllLoaded;
        img.onerror = () => {
          setImageError(true);
          checkAllLoaded();
        };
        img.src = src;
      });
    };
    
    preloadImages();
  }, []);
  
  const features = [
    {
      title: "UNRIVALED COLLECTION",
      description: "Curated selection of the most sought-after sneakers from around the world",
      icon: <FiStar className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" />,
      stats: [
        { number: "500+", label: "Exclusive Styles" },
        { number: "100%", label: "Authentic" },
        { number: "Limited", label: "Editions" }
      ]
    },
    {
      title: "PREMIUM EXPERIENCE",
      description: "From browsing to delivery, we offer a seamless premium shopping experience",
      icon: <FiShield className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" />,
      stats: [
        { number: "Fast", label: "Shipping" },
        { number: "Secure", label: "Payment" },
        { number: "30-Day", label: "Returns" }
      ]
    },
    {
      title: "COLLECTOR'S GRAILS",
      description: "Find those rare pairs you've been searching for, verified by sneaker experts",
      icon: <FiBox className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" />,
      stats: [
        { number: "Rare", label: "Collaborations" },
        { number: "Limited", label: "Editions" },
        { number: "Exclusive", label: "Releases" }
      ]
    }
  ];
  
  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Loading indicator for slow connections */}
      {!imagesLoaded && !imageError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-white/80 text-sm">Loading experience...</p>
          </div>
        </div>
      )}
      
      {/* Enhanced custom cursor follower - only for desktop */}
      {!isMobile && (
        <>
          <div 
            ref={cursorRef} 
            className="fixed w-24 h-24 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 opacity-10 pointer-events-none mix-blend-plus-lighter z-50 blur-2xl will-change-transform"
            style={{ 
              top: -12, 
              left: -12,
              transition: 'transform 0.08s cubic-bezier(0.23, 1, 0.32, 1)',
              transform: `translate3d(${mousePosition.x}px, ${mousePosition.y}px, 0)`
            }}
          ></div>
          
          {/* Text cursor follower */}
          <div 
            ref={cursorTextRef} 
            className="fixed text-xs text-white/50 pointer-events-none z-50 font-light tracking-widest opacity-70 will-change-transform"
            style={{ 
              top: 0, 
              left: 0,
              transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)',
              transform: 'translate3d(0, 0, 0)'
            }}
          >
            GRAILED
          </div>
        </>
      )}
      
      {/* Floating sneaker silhouette - only for desktop */}
      {!isMobile && (
        <div className="fixed top-10 right-10 w-40 h-40 pointer-events-none z-20 hidden lg:block">
          <div className="relative w-full h-full perspective-[1000px] transform-style-3d">
            <div 
              className="absolute inset-0 transform-style-3d animate-float-rotate will-change-transform"
            >
              <svg viewBox="0 0 100 100" className="w-full h-full fill-current text-white opacity-5">
                <path d="M95,45c-0.6-1.4-1.6-2.5-2.9-3.3c-1.8-1-4-1.2-6-0.6c-1.5-2.3-3.3-4.5-5.4-6.4c-2-1.9-4.2-3.4-6.6-4.7 c-2.8-1.5-5.8-2.5-8.8-2.9c-3-0.4-6-0.2-8.9,0.6c-2.8,0.8-5.4,2.2-7.6,4.2c-1.9,1.8-3.5,3.9-4.6,6.3c-0.7,1.5-1.2,3.1-1.5,4.8 c-0.2,1-0.2,2-0.2,3c0,0.5,0,1,0.1,1.5c-0.2,0-0.4,0-0.6,0c-0.7,0-1.4,0-2.1,0c-1.4,0-2.8,0-4.2,0c-2.8,0-5.5,0-8.3,0 c-2.8,0-5.7,0-8.5,0c-2.9,0-5.7,0-8.6,0c-2.9,0-5.8,0-8.6,0c-1.4,0-2.9,0-4.3,0c-0.7,0-1.4,0-2.1,0c-0.3,0-0.6,0-0.9,0l-0.3,0 l-0.1,0l-0.1,0c-1.4,0-2.8,0-4,1c-1.3,0.6-2.4,1.5-3.2,2.6c-0.9,1.1-1.4,2.3-1.7,3.7c-0.2,1.3-0.1,2.7,0.2,4 c0.3,1.3,1,2.5,1.8,3.5c0.9,1,1.9,1.8,3.1,2.3c1.2,0.5,2.4,0.8,3.6,0.8c0.3,0,0.6,0,0.9,0c0.3,0,0.6,0,0.9-0.1 c0.3,0,0.6-0.1,0.9-0.1c0.3,0,0.6-0.1,0.9-0.2c1.1-0.2,2.3-0.5,3.4-0.7c1.1-0.2,2.2-0.4,3.3-0.6c1.1-0.2,2.2-0.4,3.4-0.5 c1.1-0.2,2.2-0.3,3.4-0.5c1.1-0.1,2.2-0.3,3.3-0.4c1.1-0.1,2.2-0.2,3.3-0.3c1.1-0.1,2.2-0.1,3.2-0.2 c1.1-0.1,2.1-0.1,3.2-0.1c1.1,0,2.1-0.1,3.2-0.1c1.1,0,2.1,0,3.2,0c2.1,0,4.3,0.1,6.4,0.2c2.1,0.1,4.2,0.3,6.3,0.6 c2.1,0.3,4.1,0.6,6.2,1c2.1,0.4,4.1,0.9,6.2,1.4l3.1,0.8c1,0.3,2.1,0.6,3.1,0.9c1,0.3,2.1,0.7,3.1,1c1.1,0.3,2.1,0.7,3.2,1.1 c1.1,0.3,2.1,0.7,3.2,1.1c1.1,0.4,2.1,0.8,3.2,1.2c2.1,0.8,4.2,1.7,6.2,2.6c2,0.9,4,1.9,5.8,3c0.4,0.3,0.9,0.5,1.3,0.8 c0.4,0.3,0.9,0.5,1.3,0.8c0.8,0.6,1.6,1.1,2.4,1.7c0.8,0.6,1.5,1.2,2.1,1.8c0.6,0.6,1.2,1.3,1.7,1.9c0.2,0.3,0.5,0.7,0.7,1 c0.2,0.3,0.4,0.7,0.5,1c0.3,0.7,0.5,1.3,0.7,1.8c0.1,0.5,0.1,0.8,0.2,1c0,0.1,0.1,0.2,0.1,0.2c0,0,0,0,0,0l0,0 c0.8,1.9,2.2,3.4,4,4.4c1.8,0.9,3.9,1.3,5.9,0.9c2-0.3,3.9-1.3,5.3-2.8c1.4-1.5,2.3-3.5,2.5-5.5c0.2-2.1-0.3-4.1-1.3-5.9 c-1-1.8-2.5-3.2-4.3-4.1c-0.9-0.4-1.8-0.7-2.8-0.8c-1-0.1-1.9-0.1-2.9,0.1c-0.9-3.2-2.2-6.3-3.7-9.2c-1.4-2.9-3.1-5.6-5-8.1 c-1.9-2.5-4-4.8-6.4-6.9c-0.6-0.5-1.2-1-1.8-1.5c-0.3-0.2-0.6-0.5-0.9-0.7l-0.9-0.7c-1.2-0.9-2.5-1.7-3.8-2.4 c-1.3-0.7-2.6-1.3-4-1.9c-1.4-0.5-2.7-1-4.1-1.3c-1.4-0.3-2.8-0.6-4.2-0.7c-0.7-0.1-1.4-0.1-2.1-0.1c-0.7,0-1.4,0-2.1,0.1l-0.2,0 c0.1-0.8,0.2-1.5,0.4-2.3c0.4-1.5,0.9-2.9,1.6-4.2c1-1.9,2.3-3.6,3.8-5c1.8-1.6,3.9-2.7,6.2-3.4c2.4-0.7,4.9-0.8,7.4-0.5 c2.5,0.3,5,1.2,7.4,2.4c2.3,1.2,4.3,2.6,6.1,4.3c1.8,1.7,3.4,3.7,4.7,5.8c0.2,0.3,0.4,0.5,0.6,0.8c-1.3-0.3-2.6-0.4-3.9-0.3 c-1.4,0.1-2.7,0.5-3.9,1.2c-1.2,0.7-2.2,1.6-3,2.7c-0.8,1.1-1.3,2.4-1.5,3.7c-0.2,1.3-0.1,2.7,0.3,4c0.4,1.3,1.1,2.5,2,3.5 c0.9,1,2,1.8,3.2,2.3c1.2,0.5,2.5,0.8,3.8,0.8c1.3,0,2.6-0.3,3.8-0.8c1.2-0.5,2.3-1.3,3.2-2.3c0.9-1,1.6-2.2,2-3.5 C95.1,47.7,95.2,46.3,95,45z"/>
              </svg>
            </div>
          </div>
        </div>
      )}
      
      {/* Scanlines effect - reduced on mobile */}
      <div className={`fixed inset-0 z-10 pointer-events-none ${isMobile ? 'opacity-3' : 'opacity-5'}`} style={{ 
        backgroundImage: isMobile ? 
          'repeating-linear-gradient(0deg, rgba(255,255,255,0.05), rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px)' :
          'repeating-linear-gradient(0deg, rgba(255,255,255,0.1), rgba(255,255,255,0.1) 1px, transparent 1px, transparent 2px)',
        backgroundSize: isMobile ? '100% 3px' : '100% 2px'
      }}></div>
      
      {/* Main Teaser Section */}
      <main className="relative min-h-screen flex flex-col items-center justify-start py-8 md:py-12 overflow-hidden">
        {/* Background Image with lazy loading and fallback for mobile */}
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="relative w-full h-full">
            <img
              src="/494858416_2170860486681456_7057137133348816565_n.jpg"
              alt="Background"
              loading="eager"
              className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-[100%] min-h-[100%] w-auto h-auto object-cover opacity-60 animate-float will-change-transform ${imagesLoaded ? 'opacity-60' : 'opacity-0'} transition-opacity duration-500`}
              style={{ 
                transform: isMobile ? 
                  'translate(-50%, -50%)' : 
                  `translate(-50%, calc(-50% + ${scrollY * 0.1}px))` 
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                setImageError(true);
              }}
            />
            
            {/* Fallback background for image loading errors */}
            {imageError && (
              <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-black to-black"></div>
            )}
            
            <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95"></div>
          </div>
        </div>

        {/* Improved background elements with fluid animation - reduced on mobile */}
        <div className="absolute inset-0 flex items-center justify-center z-0 overflow-hidden">
          <div 
            className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08),transparent_50%)]"
            style={{ 
              transform: isMobile ? 
                'none' : /* No transform on mobile for better performance */
                `scale(${1.1 + Math.sin(scrollY * 0.001) * 0.1})` 
            }}
          ></div>
          
          {/* Reduced animations for mobile */}
          <div 
            className={`absolute -left-64 top-1/3 w-[600px] h-[600px] bg-purple-900/10 rounded-full filter blur-[100px] opacity-30 ${isMobile ? '' : 'animate-blob-slow'}`}
            style={{ transform: 'none' }} /* Remove transform for better performance */
          ></div>
          <div 
            className={`absolute -right-64 bottom-1/3 w-[600px] h-[600px] bg-indigo-900/10 rounded-full filter blur-[100px] opacity-30 ${isMobile ? '' : 'animate-blob-delay'}`}
            style={{ transform: 'none' }} /* Remove transform for better performance */
          ></div>
          
          {/* Simplified animated particles for mobile */}
          {!isMobile ? (
            <div className="absolute inset-0 z-0 opacity-20">
              {[...Array(20)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute rounded-full bg-purple-900/40"
                  style={{
                    width: `${Math.random() * 4 + 2}px`,
                    height: `${Math.random() * 4 + 2}px`,
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `float-dot ${Math.random() * 10 + 10}s infinite ease-in-out`,
                    animationDelay: `${Math.random() * 5}s`
                  }}
                ></div>
              ))}
            </div>
          ) : (
            <div className="absolute inset-0 z-0 opacity-10">
              {[...Array(8)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute rounded-full bg-purple-900/40"
                  style={{
                    width: `${Math.random() * 3 + 1}px`,
                    height: `${Math.random() * 3 + 1}px`,
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`
                  }}
                ></div>
              ))}
            </div>
          )}
        </div>
        
        {/* Animated grid lines with moving effect - simplified on mobile */}
        <div className="absolute inset-0 z-0 opacity-5">
          <div className={`h-full w-full ${isMobile ? '' : 'animate-grid-movement'}`} style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: isMobile ? '120px 120px' : '80px 80px' // Larger grid on mobile
          }}></div>
        </div>
        
        {/* Morphing fluid background element - disabled on mobile */}
        {!isMobile && (
          <div className="absolute h-[40vw] w-[40vw] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 opacity-10">
            <div 
              className="h-full w-full bg-gradient-to-tr from-purple-900/20 via-indigo-900/20 to-violet-900/20 animate-fluid rounded-[60%_40%_30%_70%/60%_30%_70%_40%] mix-blend-multiply will-change-transform"
              style={{ filter: 'blur(60px)' }}
            ></div>
          </div>
        )}
        
        {/* Add animated flowing lines in the background - simplified on mobile */}
        {!isMobile && (
          <div className="absolute inset-0 z-0 opacity-5 overflow-hidden">
            <div className="absolute inset-0 animate-flow-movement" 
              style={{
                backgroundImage: `repeating-linear-gradient(45deg, rgba(124, 58, 237, 0.1) 0px, rgba(124, 58, 237, 0.1) 1px, transparent 1px, transparent 60px)`,
                backgroundSize: '120px 120px'
              }}
            ></div>
            <div className="absolute inset-0 animate-flow-movement-reverse" 
              style={{
                backgroundImage: `repeating-linear-gradient(-45deg, rgba(99, 102, 241, 0.1) 0px, rgba(99, 102, 241, 0.1) 1px, transparent 1px, transparent 60px)`,
                backgroundSize: '120px 120px'
              }}
            ></div>
          </div>
        )}
        
        {/* Header with logo and tagline - improved mobile styling */}
        <header className="relative z-30 w-full max-w-7xl mx-auto px-4 pt-6 md:pt-8 mb-8 md:mb-16">
          <div className="text-center mb-2">
            <div className="mb-3 text-purple-400 tracking-[0.5em] text-xs font-light" data-aos="fade-up">
              COMING SOON
            </div>
            
            {/* 3D-style title with better mobile optimization */}
            <div className="perspective-title mb-4 md:mb-6" data-aos="zoom-in">
              <h1 
                className="text-4xl sm:text-5xl md:text-[8rem] font-black tracking-tighter leading-none text-transparent relative overflow-hidden"
                style={{ letterSpacing: '-0.025em' }}
              >
                {/* Optimize text display for mobile */}
                <div className="relative">
                  {/* Base layer */}
                  <span className="absolute top-0 left-0 text-indigo-800/20 blur-[2px]">
                    <span className="hidden sm:inline">GRAILED804.COM</span>
                    <span className="inline sm:hidden">GRAILED<br />804.COM</span>
                  </span>
                  
                  {/* Middle layer */}
                  <span className="absolute top-[1px] left-[1px] text-purple-600/20 blur-[0.5px]">
                    <span className="hidden sm:inline">GRAILED804.COM</span>
                    <span className="inline sm:hidden">GRAILED<br />804.COM</span>
                  </span>
                  
                  {/* Main visible layer with gradient */}
                  <span className="relative">
                    {/* Base gradient text */}
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-400 to-indigo-500 drop-shadow-[0_0_30px_rgba(147,51,234,0.3)]">
                      <span className="hidden sm:inline">GRAILED<span className="text-white font-black drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">804</span>.COM</span>
                      <span className="inline sm:hidden">GRAILED<br /><span className="text-white font-black drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">804</span>.COM</span>
                    </span>
                    
                    {/* Shimmer effect overlay */}
                    <span className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-white/30 to-purple-400/0 bg-[length:200%_100%] animate-shimmer"></span>
                  </span>
                </div>
              </h1>
            </div>
            <div className="w-24 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 mx-auto mb-5" data-aos="zoom-in" data-aos-delay="200"></div>
            <p 
              className="text-lg sm:text-xl md:text-2xl text-white/90 font-light tracking-wide max-w-2xl mx-auto mb-5 px-4"
              data-aos="fade-up" 
              data-aos-delay="300"
            >
              Premium Sneaker Destination
            </p>
            
            <div 
              className="flex items-center justify-center gap-2 py-2 px-5 bg-white/5 backdrop-blur-lg border border-white/10 rounded-full w-fit mx-auto mb-8"
              data-aos="fade-up" 
              data-aos-delay="400"
            >
              <FiClock className="text-purple-400 w-4 h-4" />
              <span className="text-sm font-medium tracking-widest text-white/90">DROPPING SOON</span>
            </div>
          </div>
        </header>
        
        {/* Main content area with improved mobile spacing */}
        <div className="relative z-30 w-full max-w-7xl mx-auto px-4 mb-8 md:mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-start">
            {/* Left column - Feature highlights */}
            <div className="order-2 lg:order-1" data-aos="fade-right">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 tracking-tight">
                Your Ultimate Destination for <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">Rare & Exclusive Sneakers</span>
              </h2>
              
              {/* Feature tabs selector with better scrolling on mobile */}
              <div className="mb-6 md:mb-8 transform hover:translate-y-[-5px] transition-all duration-300">
                <div className="flex mb-4 md:mb-6 space-x-1 p-1 bg-white/5 backdrop-blur-md rounded-lg overflow-x-auto scrollbar-none -mx-1 px-1">
                  {features.map((feature, index) => (
                    <button
                      key={index}
                      className={`flex-1 min-w-[100px] py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium tracking-wide rounded-md transition-all duration-300 ${activeSectionIndex === index 
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' 
                        : 'text-white/70 hover:text-white'}`}
                      onClick={() => setActiveSectionIndex(index)}
                    >
                      {feature.title.split(' ')[0]}
                    </button>
                  ))}
                </div>
                
                {/* Active feature content with enhanced styling */}
                <div className="relative p-4 sm:p-8 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 overflow-hidden group">
                  <div className="absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-r from-purple-600/5 to-indigo-600/5"></div>
                  
                  {/* Video-like gradient animation in background */}
                  <div className="absolute inset-0 z-0 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 via-indigo-800/10 to-purple-900/10 animate-gradient-x"></div>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(168,85,247,0.1),transparent_70%)]"></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-6">
                      <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 group">
                        {features[activeSectionIndex].icon}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-2 text-center sm:text-left">{features[activeSectionIndex].title}</h3>
                        <p className="text-white/70 text-sm sm:text-base text-center sm:text-left">{features[activeSectionIndex].description}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-8">
                      {features[activeSectionIndex].stats.map((stat, idx) => (
                        <div key={idx} className="text-center p-3 bg-black/20 rounded-lg backdrop-blur-sm border border-white/5 hover:border-purple-500/30 transition-colors duration-300 transform hover:translate-y-[-5px] hover:shadow-purple-500/20 hover:shadow-lg transition-all duration-300">
                          <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">{stat.number}</div>
                          <div className="text-xs text-white/60 uppercase tracking-wider">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Trust badges with enhanced styling */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex flex-col items-center p-4 rounded-lg bg-white/5 backdrop-blur-md hover:bg-white/10 transition-colors duration-300 border border-white/5 hover:border-purple-500/30 transform hover:translate-y-[-5px] transition-all duration-300">
                  <FiCheck className="text-purple-400 w-5 h-5 mb-2" />
                  <span className="text-xs text-center text-white/80">100% Authentic</span>
                </div>
                <div className="flex flex-col items-center p-4 rounded-lg bg-white/5 backdrop-blur-md hover:bg-white/10 transition-colors duration-300 border border-white/5 hover:border-purple-500/30 transform hover:translate-y-[-5px] transition-all duration-300">
                  <FiCreditCard className="text-purple-400 w-5 h-5 mb-2" />
                  <span className="text-xs text-center text-white/80">Secure Payments</span>
                </div>
                <div className="flex flex-col items-center p-4 rounded-lg bg-white/5 backdrop-blur-md hover:bg-white/10 transition-colors duration-300 border border-white/5 hover:border-purple-500/30 transform hover:translate-y-[-5px] transition-all duration-300">
                  <FiTruck className="text-purple-400 w-5 h-5 mb-2" />
                  <span className="text-xs text-center text-white/80">Fast Shipping</span>
                </div>
                <div className="flex flex-col items-center p-4 rounded-lg bg-white/5 backdrop-blur-md hover:bg-white/10 transition-colors duration-300 border border-white/5 hover:border-purple-500/30 transform hover:translate-y-[-5px] transition-all duration-300">
                  <FiBox className="text-purple-400 w-5 h-5 mb-2" />
                  <span className="text-xs text-center text-white/80">Easy Returns</span>
                </div>
              </div>
            </div>
            
            {/* Right column - Email signup with better mobile padding */}
            <div className="order-1 lg:order-2" data-aos="fade-left">
              <div className="relative p-5 sm:p-6 md:p-8 rounded-2xl overflow-hidden backdrop-blur-xl border border-white/10 shadow-[0_0_50px_rgba(124,58,237,0.1)] animate-border-glow transform hover:translate-y-[-5px] transition-all duration-500">
                {/* Enhanced animated background */}
                <div className="absolute inset-0 z-0 opacity-30">
                  <div 
                    className="absolute -inset-[100px] bg-gradient-conic from-indigo-500/40 via-purple-600/30 to-indigo-500/40 opacity-50 blur-3xl"
                    style={{ transform: 'rotate(45deg)' }}
                  ></div>
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg">
                      <FiShoppingBag className="text-white w-5 h-5" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Get Early Access</h2>
                  </div>
                  <p className="text-white/70 mb-6 text-sm sm:text-base">Be the first to know when we launch. Sign up for exclusive offers and early access.</p>
                  
                  <form onSubmit={handleSubscribe} className="relative mb-4">
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-white/10 text-white border border-white/20 rounded-lg py-3 px-4 outline-none focus:border-purple-500/70 transition-all duration-300 backdrop-blur-xl text-sm placeholder:text-white/30"
                      autoComplete="email"
                    />
                    <button
                      type="submit"
                      className="absolute right-1 top-1 bottom-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg px-3 sm:px-5 flex items-center justify-center transition-all duration-300 group font-medium text-sm"
                      aria-label="Subscribe"
                    >
                      <span className="mr-1 sm:mr-2 group-hover:mr-2 sm:group-hover:mr-3 transition-all duration-300 hidden sm:inline">Subscribe</span>
                      <span className="sm:hidden">Join</span>
                      <FiArrowRight className="group-hover:translate-x-1 transition-all duration-300" />
                    </button>
                  </form>
                  
                  {emailError && <p className="text-red-400 text-xs sm:text-sm mb-4">{emailError}</p>}
                  {subscribed && (
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg px-4 py-3 mb-4 text-green-400 flex items-center text-xs sm:text-sm">
                      <FiCheck className="mr-2 flex-shrink-0" /> 
                      <span>Thanks for subscribing! We'll keep you updated.</span>
                    </div>
                  )}
                  
                  {/* Social media section with improved visibility */}
                  <div className="border-t border-white/10 pt-5 mt-5">
                    <div className="flex justify-center gap-4 sm:gap-6">
                      <a 
                        href="https://www.instagram.com/grailed804/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="group flex flex-col items-center"
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/20 hover:border-purple-500/70 hover:bg-purple-900/20 transform hover:scale-110 transition-all duration-300 shadow-[0_0_15px_rgba(168,85,247,0.25)]">
                          <FiInstagram className="text-white w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform duration-300" />
                        </div>
                        <span className="text-xs text-white/60 mt-2 group-hover:text-white/80 transition-colors duration-300">Instagram</span>
                      </a>
                      <a 
                        href="https://www.whatnot.com/user/grailed804" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="group flex flex-col items-center"
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/20 hover:border-purple-500/70 hover:bg-purple-900/20 transform hover:scale-110 transition-all duration-300 shadow-[0_0_15px_rgba(168,85,247,0.25)]">
                          <FiTag className="text-white w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform duration-300" />
                        </div>
                        <span className="text-xs text-white/60 mt-2 group-hover:text-white/80 transition-colors duration-300">Whatnot</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer with better mobile spacing */}
        <footer className="relative z-30 w-full py-4 md:py-6 border-t border-white/10">
          <div className="text-center">
            <div className="text-white/60 text-xs font-light tracking-wide">
              &copy; {new Date().getFullYear()} GRAILED804. All rights reserved.
            </div>
          </div>
        </footer>
      </main>
      
      {/* Optimized audio controls for mobile */}
      <div className={`fixed bottom-4 left-4 z-50 ${isMobile ? 'scale-90 origin-bottom-left' : ''}`}>
        <div className="bg-black/80 backdrop-blur-lg rounded-lg border border-white/10 p-2 md:p-3 shadow-lg animate-border-glow">
          <div className="flex items-center gap-2 md:gap-3">
            {/* Play/Pause Button */}
            <button 
              onClick={togglePlay}
              className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center group shadow-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300"
            >
              {isPlaying ? (
                <FiPause className="text-white w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              ) : (
                <FiPlay className="text-white w-5 h-5 group-hover:scale-110 transition-transform duration-300 ml-0.5" />
              )}
            </button>
            
            {/* Mobile Mute/Unmute Button - Always visible regardless of screen size */}
            <button 
              onClick={toggleMute}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group transition-all duration-300 border border-white/20"
              aria-label={isMuted ? "Unmute music" : "Mute music"}
            >
              {isMuted ? (
                <FiVolumeX className="text-white w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              ) : (
                <FiVolume2 className="text-white w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              )}
            </button>
            
            {/* Song Info */}
            <div className="flex flex-col max-w-[120px] sm:max-w-none">
              <div className="text-xs font-medium text-white/90 flex items-center gap-1.5 truncate">
                <FiMusic className="w-3 h-3 text-purple-400 flex-shrink-0" />
                <span className="truncate">Travis Scott - The Scotts</span>
              </div>
              <div className="text-xs text-white/50 truncate">
                {isMuted ? (
                  <span className="text-red-400 font-semibold">Music muted - tap <FiVolumeX className="inline w-3 h-3" /> to unmute</span>
                ) : (
                  "Playing in background • 0:45"
                )}
              </div>
            </div>
            
            {/* Volume Slider - Desktop only */}
            <div className="hidden sm:flex items-center gap-2 ml-2">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-16 h-1.5 appearance-none bg-white/20 rounded-full outline-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, rgba(168, 85, 247, 0.8) 0%, rgba(168, 85, 247, 0.8) ${volume * 100}%, rgba(255, 255, 255, 0.2) ${volume * 100}%, rgba(255, 255, 255, 0.2) 100%)`
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Hidden YouTube Player */}
        <div className="hidden">
          <iframe 
            id="youtube-player" 
            ref={youtubePlayerRef}
            width="560" 
            height="315" 
            src="https://www.youtube-nocookie.com/embed/sw4r0k8WWqU?si=EGG4xOGya8gFdlxH&start=20&autoplay=1&mute=1&enablejsapi=1&origin=https://grailed804.com" 
            title="YouTube video player" 
            frameBorder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerPolicy="strict-origin-when-cross-origin" 
            allowFullScreen
          ></iframe>
        </div>
      </div>
      
      {/* Add CSS for animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes gradient-conic {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        
        @keyframes shine {
          from {
            background-position: -200% center;
          }
          to {
            background-position: 200% center;
          }
        }
        
        @keyframes shimmer {
          0% {
            background-position: -100% 0;
          }
          100% {
            background-position: 100% 0;
          }
        }
        
        @keyframes border-glow {
          0% {
            box-shadow: 0 0 20px 0px rgba(124, 58, 237, 0.1);
          }
          50% {
            box-shadow: 0 0 30px 4px rgba(124, 58, 237, 0.2);
          }
          100% {
            box-shadow: 0 0 20px 0px rgba(124, 58, 237, 0.1);
          }
        }
        
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(20px, -30px) scale(1.05);
          }
          50% {
            transform: translate(-20px, 20px) scale(0.95);
          }
          75% {
            transform: translate(20px, 30px) scale(1.05);
          }
        }
        
        @keyframes blob-slow {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(-50px, 50px) scale(1.1);
          }
          66% {
            transform: translate(50px, 20px) scale(0.9);
          }
        }
        
        @keyframes blob-delay {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(70px, -30px) scale(1.15);
          }
          66% {
            transform: translate(-20px, 40px) scale(0.85);
          }
        }
        
        @keyframes fluid {
          0%, 100% {
            border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
          }
          25% {
            border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
          }
          50% {
            border-radius: 40% 60% 70% 30% / 60% 70% 40% 30%;
          }
          75% {
            border-radius: 60% 30% 40% 70% / 30% 30% 70% 60%;
          }
        }
        
        @keyframes fluid-slow {
          0% {
            transform: translate(0, 0) rotate(0deg);
          }
          20% {
            transform: translate(10px, 10px) rotate(2deg);
          }
          40% {
            transform: translate(-15px, 5px) rotate(-1deg);
          }
          60% {
            transform: translate(7px, -10px) rotate(1deg);
          }
          80% {
            transform: translate(-5px, 15px) rotate(-2deg);
          }
          100% {
            transform: translate(0, 0) rotate(0deg);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            transform: translate(-50%, calc(-50% + 10px)) scale(1.02);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        
        .animate-border-glow {
          animation: border-glow 4s ease-in-out infinite;
        }
        
        .bg-gradient-conic {
          animation: gradient-conic 10s linear infinite;
        }
        
        .animate-fluid {
          animation: fluid 8s ease-in-out infinite;
        }
        
        .animate-fluid-slow {
          animation: fluid-slow 20s ease-in-out infinite;
        }
        
        .animate-blob {
          animation: blob 8s ease-in-out infinite;
        }
        
        .animate-blob-slow {
          animation: blob-slow 12s ease-in-out infinite;
        }
        
        .animate-blob-delay {
          animation: blob-delay 12s ease-in-out 3s infinite;
        }
        
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        
        [data-aos="zoom-in"] {
          opacity: 0;
          transform: scale(0.8);
          transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        [data-aos="zoom-in"].aos-animate {
          opacity: 1;
          transform: scale(1);
        }
        
        [data-aos="fade-up"] {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        [data-aos="fade-up"].aos-animate {
          opacity: 1;
          transform: translateY(0);
        }
        
        [data-aos="fade-right"] {
          opacity: 0;
          transform: translateX(-40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        [data-aos="fade-right"].aos-animate {
          opacity: 1;
          transform: translateX(0);
        }
        
        [data-aos="fade-left"] {
          opacity: 0;
          transform: translateX(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        [data-aos="fade-left"].aos-animate {
          opacity: 1;
          transform: translateX(0);
        }
        
        @keyframes gradient-x {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        .animate-gradient-x {
          background-size: 200% 100%;
          animation: gradient-x 15s ease infinite;
        }
        
        @keyframes float-rotate {
          0% {
            transform: rotateX(10deg) rotateY(0deg);
          }
          50% {
            transform: rotateX(-5deg) rotateY(20deg);
          }
          100% {
            transform: rotateX(10deg) rotateY(0deg);
          }
        }
        
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        
        .perspective-\\[1000px\\] {
          perspective: 1000px;
        }
        
        .animate-float-rotate {
          animation: float-rotate 15s ease-in-out infinite;
        }
        
        @keyframes float-dot {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }
        
        .perspective-title {
          perspective: 1000px;
          transform-style: preserve-3d;
          transform: rotateX(10deg);
          transition: transform 0.5s ease;
        }
        
        .perspective-title:hover {
          transform: rotateX(15deg) rotateY(-5deg);
        }
        
        /* Custom range input styling */
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
        }
        
        @keyframes flow-movement {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 120px 120px;
          }
        }
        
        @keyframes flow-movement-reverse {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: -120px 120px;
          }
        }
        
        @keyframes grid-movement {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 80px 80px;
          }
        }
        
        .animate-flow-movement {
          animation: flow-movement 15s linear infinite;
        }
        
        .animate-flow-movement-reverse {
          animation: flow-movement-reverse 15s linear infinite;
        }
        
        .animate-grid-movement {
          animation: grid-movement 30s linear infinite;
        }
      ` }} />
    </div>
  );
};

export default TeaserPage;
